import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn'

type CardTone = 'surface' | 'muted' | 'ink' | 'sage'

type CardProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode
  padded?: boolean
  /** surface: cream with a soft shadow. muted: sand, flat. ink: the dark rail colour. sage: pale green. */
  tone?: CardTone
}

const toneStyles: Record<CardTone, string> = {
  surface: 'bg-surface shadow-card',
  muted: 'bg-surface-muted',
  ink: 'bg-ink text-gray-50',
  sage: 'bg-sage-50 text-sage-900',
}

export function Card({ className, children, padded = true, tone = 'surface', ...rest }: CardProps) {
  return (
    <div className={cn('rounded-lg', toneStyles[tone], padded && 'p-6', className)} {...rest}>
      {children}
    </div>
  )
}

export function CardHeader({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('mb-4 flex items-start justify-between gap-3', className)}>{children}</div>
}

export function CardTitle({ className, children }: { className?: string; children: ReactNode }) {
  return <h2 className={cn('font-display text-[19px] font-normal', className)}>{children}</h2>
}

export function CardSubtitle({ className, children }: { className?: string; children: ReactNode }) {
  return <p className={cn('mt-0.5 text-[13px] text-gray-600', className)}>{children}</p>
}

/** The small uppercase label the system uses above numbers and table columns. */
export function CardKicker({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <p className={cn('text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500', className)}>
      {children}
    </p>
  )
}
